import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { products } from '../../../../shared/data/product-data';
import { BlueOcean } from '../../../../shared/models/blueocean-product';
import { ApplicationState } from '../../../../shared/state/application-state.service';
import { LoadingComponent } from '../../../../shared/ui/loading/loading.component';
import { NotificationCard } from '../../feature/notification-card/notification-card.component';
import { ReportCardComponent } from '../../feature/report-card/report-card.component';
import { WorkspaceSelector } from '../../feature/workspace-selector/workspace-selector.component';
import { PowerBiApi } from '../../models/power-bi-models';
import { PowerBiState } from '../../state/power-bi-state.service';

@Component({
  selector: 'app-home',
  imports: [
    LoadingComponent,
    ReportCardComponent,
    RouterModule,
    WorkspaceSelector,
    NotificationCard
  ],
  standalone: true,
  templateUrl: './home.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeComponent {
  availableProducts: BlueOcean.Product[] = products;

  applicationState = inject(ApplicationState);
  powerBiState = inject(PowerBiState);
  loginError = this.applicationState.loginError;

  router = inject(Router);
  route = inject(ActivatedRoute);
  authService = inject(MsalService);
  title = inject(Title);

  activeAccount = this.applicationState.activeAccount;
  // Workspaces
  activeWorkspace = this.powerBiState.selectedWorkspace;
  currentWorkspaces = this.powerBiState.workspaces;
  loadingWorkspaces = this.powerBiState.loadingWorkspaces;
  workspacesError = this.powerBiState.workspacesError;

  // Reports
  showAllReports = signal<boolean>(false);
  showRCs = signal<boolean>(true);
  reports = this.powerBiState.reports;

  visibleReports = computed(() => {
    const reports = this.reports();
    if (this.applicationState.isReader() && this.showAllReports()) {
      return reports;
    }
    // Filter out reports that do not correspond to any available product 
    return reports.filter((report: PowerBiApi.Report) => this.getProductFromReport(report) != null);
  });

  loadingReports = this.powerBiState.loadingReports;
  reportsError = this.powerBiState.reportsError;
  // Computed State
  loading = computed(() => this.loadingReports() || this.loadingWorkspaces());
  notBoughtProducts = computed(() => this.powerBiState.availableNotBoughtProducts().filter(p => p.id !== ''));

  constructor() {
    effect(() => {
      const workspace = this.activeWorkspace();
      if (!workspace) return;
      const workspaceName = workspace.name.split('_').slice(1).join('_');
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { workspace: workspaceName },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      this.title.setTitle(`Cloud Insights | ${workspace.normalizedName}`);
    });
  }

  ngOnInit(): void {
    const workspace = this.route.snapshot.queryParamMap.get('workspace');
    if (workspace) this.powerBiState.requestWorkspace(workspace);
  }

  getProductFromReport(report: PowerBiApi.Report): BlueOcean.Product | null {
    const reportName = report.name.toLocaleLowerCase();
    return this.availableProducts.find(product => {
      const productName = product.name.toLocaleLowerCase();
      return reportName === productName || reportName.startsWith(productName + ' ');
    }) ?? null;
  }

  goToProduct(product: string) {
    this.router.navigate([`/product/${product}`]);
  }

  goToContact() {
    this.router.navigate(['/contact']);
  }
}
